import {useMemo, useState} from 'react';
import {compact, explorerAccount, flag, healthColor, percent, shortAddress} from '../lib/format';

const COLUMNS = [
    {key: 'rank', label: '#', sort: v => v.rank, numeric: true},
    {key: 'validator', label: 'Validator', sort: v => (v.name || v.address).toLowerCase()},
    {key: 'location', label: 'Location', sort: v => v.location?.country || '~'},
    {key: 'provider', label: 'Provider', sort: v => v.location?.provider || '~'},
    {key: 'stake', label: 'Stake (APT)', sort: v => v.stake, numeric: true},
    {key: 'proposals', label: 'Proposals', sort: v => v.successRate ?? -1, numeric: true},
    {key: 'governance', label: 'Governance', sort: v => v.governanceRate ?? -1, numeric: true},
];

const PAGE_SIZE = 25;

export default function ValidatorTable({validators, governanceWindow, selected, onSelect}) {
    const [query, setQuery] = useState('');
    const [sort, setSort] = useState({key: 'stake', dir: -1});
    const [expanded, setExpanded] = useState(false);

    const ranked = useMemo(
        () => [...validators].sort((a, b) => b.stake - a.stake).map((v, i) => ({...v, rank: i + 1})),
        [validators],
    );

    const rows = useMemo(() => {
        const q = query.trim().toLowerCase();
        const col = COLUMNS.find(c => c.key === sort.key);
        return ranked
            .filter(v => !q || [v.name, v.address, v.operator, v.location?.city, v.location?.country, v.location?.provider, v.location?.host]
                .some(f => f && f.toLowerCase().includes(q)))
            .sort((a, b) => {
                const x = col.sort(a);
                const y = col.sort(b);
                return (x < y ? -1 : x > y ? 1 : 0) * sort.dir;
            });
    }, [ranked, query, sort]);

    const toggleSort = key => setSort(s => ({key, dir: s.key === key ? -s.dir : (COLUMNS.find(c => c.key === key).numeric ? -1 : 1)}));

    return (
        <section className="card table-card">
            <div className="card-head">
                <div>
                    <h2>Active validator set</h2>
                    <p className="muted">{rows.length} of {validators.length} validators · select a row to find it on the globe</p>
                </div>
                <input
                    className="search"
                    type="search"
                    placeholder="Search name, address, city, provider…"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                />
            </div>
            <div className="table-scroll">
                <table>
                    <thead>
                        <tr>
                            {COLUMNS.map(c => (
                                <th key={c.key} className={c.numeric ? 'num' : undefined} aria-sort={sort.key === c.key ? (sort.dir > 0 ? 'ascending' : 'descending') : 'none'}>
                                    <button onClick={() => toggleSort(c.key)}>
                                        {c.label}
                                        <span className="sort-icon">{sort.key === c.key ? (sort.dir > 0 ? '↑' : '↓') : ''}</span>
                                    </button>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {(expanded || query ? rows : rows.slice(0, PAGE_SIZE)).map(v => {
                            const l = v.location || {};
                            const total = v.successful + v.failed;
                            return (
                                <tr key={v.address} className={selected === v.address ? 'selected' : undefined} onClick={() => onSelect(v)}>
                                    <td className="num muted">{v.rank}</td>
                                    <td>
                                        <div className="validator-cell">
                                            <span className="dot" style={{background: healthColor(v.successRate)}} />
                                            <div>
                                                <a href={explorerAccount(v.address)} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                                                    {v.name || shortAddress(v.address)}
                                                </a>
                                                <div className="sub mono">{v.name ? shortAddress(v.address) : l.host || `operator ${shortAddress(v.operator)}`}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        {l.country ? <>{flag(l.countryCode)} {l.city}<div className="sub">{l.country}</div></> : <span className="muted">Unknown</span>}
                                    </td>
                                    <td className="provider">{l.provider || <span className="muted">—</span>}</td>
                                    <td className="num">
                                        {compact(v.stake, 2)}
                                        <div className="sub">{percent(v.votingShare, 2)} power</div>
                                    </td>
                                    <td className="num">
                                        <span style={{color: healthColor(v.successRate)}}>{total ? percent(v.successRate) : '—'}</span>
                                        <div className="sub">{v.successful} / {total}</div>
                                    </td>
                                    <td className="num">
                                        {v.governanceRate == null ? '—' : (
                                            <>
                                                <div className="meter" aria-hidden="true"><span style={{width: percent(v.governanceRate, 0)}} /></div>
                                                <div className="sub">{v.governanceVotes} / {governanceWindow}</div>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {!rows.length && <div className="empty">No validators match “{query}”.</div>}
            </div>
            {!query && rows.length > PAGE_SIZE && (
                <button className="show-more" onClick={() => setExpanded(e => !e)}>
                    {expanded ? 'Show top 25' : `Show all ${rows.length} validators`}
                </button>
            )}
        </section>
    );
}
